/**
 * transformations.js — outillage du dossier Transformations 4ᵉ (thème #9, 180-182).
 *
 * RÉUTILISE le socle SVG du 5ᵉ (`svgTransfo` : svgGrille + flèches de vecteur
 * que ce thème vient d'ajouter) et l'enrichit de trois familles NEUVES au
 * niveau 4ᵉ :
 *   - homothétie (agrandissement / réduction d'une figure) : M' = O + k(M − O) ;
 *   - translation (vecteur v = B − A ; M' = M + v) ;
 *   - arithmétique des rapports k / k² / k³ pour les longueurs / aires / volumes.
 *
 * TOUT est exact par construction : les rapports sont des rationnels {n,d}
 * réduits, et les gens tirent des dimensions divisibles pour que ×k, ×k², ×k³
 * tombent sur des entiers (le doc du prof tolère 4/3 cm ; on l'évite pour un
 * scoring propre — adaptation validée).
 */

import {
    svgGrille, elementGrille, fr, paire, BLEU, ORANGE, ROUGE, VERT,
} from '../../5eme/transformations/svgTransfo';

export { svgGrille, elementGrille, fr, paire, BLEU, ORANGE, ROUGE, VERT };

// ─────────────────────────────────────────────────────────────
// Géométrie vectorielle (coordonnées entières de grille)
// ─────────────────────────────────────────────────────────────

/** Vecteur AB = B − A. */
export const vecteur = (A, B) => [B[0] - A[0], B[1] - A[1]];

/** Image de P par la translation de vecteur v : P + v. */
export const translater = (P, v) => [P[0] + v[0], P[1] + v[1]];

/**
 * Image de P par l'homothétie de centre O et de rapport k = n/d :
 * M' = O + k(M − O). Renvoie des entiers ; le tirage garantit la divisibilité
 * (P − O multiple de d sur chaque coordonnée).
 */
export const homothetie = (P, O, { n, d }) => [
    O[0] + (n * (P[0] - O[0])) / d,
    O[1] + (n * (P[1] - O[1])) / d,
];

/** Aire (entière) d'un polygone simple par la formule du lacet (× 1/2). */
export const airePoly = (pts) => {
    let s = 0;
    for (let i = 0; i < pts.length; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % pts.length];
        s += x1 * y2 - x2 * y1;
    }
    return Math.abs(s) / 2;
};

// ─────────────────────────────────────────────────────────────
// Arithmétique des rapports k (rationnels réduits)
// ─────────────────────────────────────────────────────────────

export const pgcd = (a, b) => (b === 0 ? Math.abs(a) : pgcd(b, a % b));

/** Réduit {n,d} (d > 0). */
export const reduire = ({ n, d }) => {
    const g = pgcd(n, d) || 1;
    return { n: n / g, d: d / g };
};

/** Rapports d'AGRANDISSEMENT (k > 1, entiers). */
export const AGRANDIS = [{ n: 2, d: 1 }, { n: 3, d: 1 }, { n: 4, d: 1 }, { n: 5, d: 1 }];
/** Rapports de RÉDUCTION (0 < k < 1, réduits). */
export const REDUC = [
    { n: 1, d: 2 }, { n: 1, d: 3 }, { n: 1, d: 5 },
    { n: 2, d: 5 }, { n: 3, d: 4 }, { n: 3, d: 5 }, { n: 4, d: 5 },
];

/** k au format fraction française "n/d" (ou "n" si d = 1). */
export const kFrac = ({ n, d }) => (d === 1 ? String(n) : `${n}/${d}`);

/** k au format décimal français EXACT, ou null si non décimal (d ≠ 2ᵃ5ᵇ). */
export const kDec = ({ n, d }) => {
    let dd = d, a = 0, b = 0;
    while (dd % 2 === 0) { dd /= 2; a++; }
    while (dd % 5 === 0) { dd /= 5; b++; }
    if (dd !== 1) return null;
    const p = Math.max(a, b);
    const num = n * (10 ** p) / d;      // entier par construction
    return fr(num / (10 ** p));
};

/** Multiplie l'entier v par le rationnel k (exact : v multiple de d requis). */
export const mulK = (v, { n, d }) => (v * n) / d;
/** Multiplie l'entier v par k² (v multiple de d²). */
export const mulK2 = (v, { n, d }) => (v * n * n) / (d * d);
/** Multiplie l'entier v par k³ (v multiple de d³). */
export const mulK3 = (v, { n, d }) => (v * n * n * n) / (d * d * d);

/** k² sous forme fraction réduite "n²/d²" (ex. (3/4)² → "9/16"). */
export const kCarreFrac = ({ n, d }) => reduireStr(n * n, d * d);
/** k³ sous forme fraction réduite. */
export const kCubeFrac = ({ n, d }) => reduireStr(n * n * n, d * d * d);

const reduireStr = (num, den) => {
    const g = pgcd(num, den) || 1;
    const a = num / g, b = den / g;
    return b === 1 ? String(a) : `${a}/${b}`;
};

// ═════════════════════════════════════════════════════════════
// Revue 2026-09-23 (vague 4, la translation) : figures des cartes 182 et 187.
//
// AJOUT SEULEMENT. Rien de ce qui précède ne change : 180 et 181 lisent ce
// module, et geometrie.js le ré-exporte (liste fermée de noms) pour 187, 188,
// 189 et 220. Le socle 5e (svgGrille, elementGrille) n'est pas touché : il sert
// aussi 951, 952, 181 et 1245.
//
// Pourquoi un second moteur plutôt que svgGrille : svgGrille pose la figure en
// imageData (un prof ne peut plus réenregistrer la question, FORMATS §12), sa
// pointe de flèche fait 7 px et se cache sous le disque du point d'arrivée, et
// ses étiquettes sont toujours à droite du point, donc posées sur la flèche
// quand elle part vers la droite. Ici : SVG en imageUrl avec un alt, trait
// arrêté avant le point d'arrivée, pointe de 10 px, étiquettes placées là où
// rien ne les gêne, noms de points en texte comme dans le reste du parc.
// ═════════════════════════════════════════════════════════════

/** Élément d'énoncé : le SVG en data-URL dans config.imageUrl (jamais imageData, FORMATS §12) et un alt qui décrit sans donner la réponse. */
export const imageEnonce = (svg, alt, maxWidth = 'medium') => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth, alt } }],
});

/** Distance du point p au segment [a, b], dans les unités des points (carreaux ou pixels). */
export const distPointSegment = (p, a, b) => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const l2 = dx * dx + dy * dy;
    const t = l2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
    return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
};

const r1 = (v) => Math.round(v * 10) / 10;
const orient = (a, b, c) => Math.sign((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));
const seCoupent = (p1, p2, p3, p4) => {
    const o1 = orient(p1, p2, p3); const o2 = orient(p1, p2, p4);
    const o3 = orient(p3, p4, p1); const o4 = orient(p3, p4, p2);
    return o1 * o2 <= 0 && o3 * o4 <= 0
        && Math.max(p1[0], p2[0]) >= Math.min(p3[0], p4[0]) && Math.max(p3[0], p4[0]) >= Math.min(p1[0], p2[0])
        && Math.max(p1[1], p2[1]) >= Math.min(p3[1], p4[1]) && Math.max(p3[1], p4[1]) >= Math.min(p1[1], p2[1]);
};
const coinsRect = (r) => [[r.x0, r.y0], [r.x1, r.y0], [r.x1, r.y1], [r.x0, r.y1]];
const distPtRect = (p, r) => Math.hypot(Math.max(r.x0 - p[0], 0, p[0] - r.x1), Math.max(r.y0 - p[1], 0, p[1] - r.y1));
const distRectSeg = (r, a, b) => {
    if (distPtRect(a, r) === 0 || distPtRect(b, r) === 0) return 0;
    const c = coinsRect(r);
    for (let i = 0; i < 4; i++) if (seCoupent(a, b, c[i], c[(i + 1) % 4])) return 0;
    return Math.min(...c.map((q) => distPointSegment(q, a, b)), distPtRect(a, r), distPtRect(b, r));
};
const distRectRect = (r, s) => Math.hypot(Math.max(s.x0 - r.x1, 0, r.x0 - s.x1), Math.max(s.y0 - r.y1, 0, r.y0 - s.y1));
const rectAutour = ([cx, cy], w, h) => ({ x0: cx - w / 2, y0: cy - h / 2, x1: cx + w / 2, y1: cy + h / 2 });

/** Place un rectangle d'étiquette parmi des centres candidats : le premier (ordre de préférence) assez dégagé, sinon le plus dégagé, toujours dans le cadre. */
const placerEtiquette = (candidats, w, h, obstacles, cadre, seuil = 6) => {
    let meilleur = null;
    for (const c of candidats) {
        const r = rectAutour(c, w, h);
        if (r.x0 < 2 || r.y0 < 2 || r.x1 > cadre.W - 2 || r.y1 > cadre.H - 2) continue;
        const d = Math.min(
            Infinity,
            ...obstacles.segs.map(([a, b]) => distRectSeg(r, a, b)),
            ...obstacles.rects.map((s) => distRectRect(r, s)),
        );
        if (d >= seuil) return { centre: c, rect: r, degagement: d };
        if (!meilleur || d > meilleur.degagement) meilleur = { centre: c, rect: r, degagement: d };
    }
    if (!meilleur) throw new Error('svgQuadrillage : étiquette hors du cadre');
    return meilleur;
};

/** Largeur (px) d'un nom de point en gras 14 px : lettres et primes. */
const largeurNom = (nom) => [...String(nom)].reduce((s, ch) => s + (/['′]/.test(ch) ? 4 : 10), 0);

const DIRECTIONS = [[1, 0], [0, 1], [-1, 0], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]];

/**
 * Quadrillage (ou repère gradué) des cartes translation, en SVG autonome.
 *   xMin, xMax, yMin, yMax : bornes entières ; pas : pixels par carreau (défaut : selon la taille) ;
 *   axes    : true → repère gradué (axes fléchés, graduations class="tx" / "ty", 0 à l'origine) ;
 *   polys   : [{ pts, couleur, dash?, opacite?, numero?, centre? }] polygones fermés, numero écrit en centre (coordonnées de grille) ;
 *   segments: [{ a, b, couleur, dash? }] ;
 *   fleches : [{ a, b, couleur?, numero? }] flèche de a vers b : trait arrêté 6 px avant un point nommé posé en b,
 *             pointe pleine de 10 px ; numero → pastille ronde numérotée posée là où rien ne la gêne ;
 *   points  : [{ x, y, nom, couleur?, pref? }] disque + nom en texte, placé hors des traits (pref : direction préférée [dx, dy]) ;
 *   data    : { cle: valeur } → attributs data-* sur la racine (relecture par les solveurs et les mesures).
 * Balisage relu : <g class="pt" data-nom data-xy="x,y"> (disque, texte class="pl"), <g class="fl" data-de="x,y"
 * data-a="x,y" data-numero?> (coordonnées de grille des extrémités VRAIES, le trait étant raccourci),
 * <text class="num"> numéro de polygone, <g class="nf" data-numero> pastille de flèche.
 */
export const svgQuadrillage = ({
    xMin = 0, xMax, yMin = 0, yMax, pas = null, axes = false,
    polys = [], segments = [], fleches = [], points = [], data = {},
}) => {
    const PAD = axes ? 30 : 18;
    const span = Math.max(xMax - xMin, yMax - yMin);
    const P = pas || Math.max(16, Math.min(32, Math.floor(460 / span)));
    const W = 2 * PAD + (xMax - xMin) * P;
    const H = 2 * PAD + (yMax - yMin) * P;
    const cadre = { W, H };
    const px = (x) => r1(PAD + (x - xMin) * P);
    const py = (y) => r1(PAD + (yMax - y) * P);
    const pix = (q) => [px(q[0]), py(q[1])];
    const obstacles = { segs: [], rects: [] };
    const out = [];
    const attrs = Object.entries(data).map(([k, v]) => ` data-${k}="${v}"`).join('');
    out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="system-ui, sans-serif"${attrs}>`);
    out.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`);
    for (let x = xMin; x <= xMax; x++) out.push(`<line x1="${px(x)}" y1="${py(yMax)}" x2="${px(x)}" y2="${py(yMin)}" stroke="#e2e8f0" stroke-width="1"/>`);
    for (let y = yMin; y <= yMax; y++) out.push(`<line x1="${px(xMin)}" y1="${py(y)}" x2="${px(xMax)}" y2="${py(y)}" stroke="#e2e8f0" stroke-width="1"/>`);

    if (axes) {
        const x0 = px(0); const y0 = py(0);
        out.push(`<line x1="${px(xMin)}" y1="${y0}" x2="${px(xMax) + 8}" y2="${y0}" stroke="#334155" stroke-width="1.6"/>`);
        out.push(`<polygon points="${px(xMax) + 12},${y0} ${px(xMax) + 4},${y0 - 4} ${px(xMax) + 4},${y0 + 4}" fill="#334155"/>`);
        out.push(`<line x1="${x0}" y1="${py(yMin)}" x2="${x0}" y2="${py(yMax) - 8}" stroke="#334155" stroke-width="1.6"/>`);
        out.push(`<polygon points="${x0},${py(yMax) - 12} ${x0 - 4},${py(yMax) - 4} ${x0 + 4},${py(yMax) - 4}" fill="#334155"/>`);
        obstacles.segs.push([[px(xMin), y0], [px(xMax) + 12, y0]], [[x0, py(yMin)], [x0, py(yMax) - 12]]);
        for (let x = xMin; x <= xMax; x++) {
            if (x === 0) continue;
            out.push(`<text class="tx" x="${px(x)}" y="${r1(y0 + 15)}" text-anchor="middle" font-size="11" fill="#1e293b">${fr(x)}</text>`);
            obstacles.rects.push(rectAutour([px(x), y0 + 11], 14, 11));
        }
        for (let y = yMin; y <= yMax; y++) {
            if (y === 0) continue;
            out.push(`<text class="ty" x="${r1(x0 - 6)}" y="${r1(py(y) + 4)}" text-anchor="end" font-size="11" fill="#1e293b">${fr(y)}</text>`);
            obstacles.rects.push({ x0: x0 - 20, y0: py(y) - 6, x1: x0 - 5, y1: py(y) + 6 });
        }
        out.push(`<text x="${r1(x0 - 6)}" y="${r1(y0 + 15)}" text-anchor="end" font-size="11" fill="#1e293b">0</text>`);
    }

    polys.forEach((pl) => {
        const q = pl.pts.map(pix);
        out.push(`<polygon class="poly" points="${q.map((c) => c.join(',')).join(' ')}" fill="${pl.couleur}" fill-opacity="${pl.opacite ?? 0.12}" stroke="${pl.couleur}" stroke-width="2"${pl.dash ? ' stroke-dasharray="6 4"' : ''}/>`);
        q.forEach((c, i) => obstacles.segs.push([c, q[(i + 1) % q.length]]));
    });
    polys.forEach((pl) => {
        if (pl.numero == null) return;
        const c = pix(pl.centre);
        out.push(`<text class="num" x="${c[0]}" y="${r1(c[1] + 5)}" text-anchor="middle" font-size="14" font-weight="700" fill="${pl.couleur}">${pl.numero}</text>`);
        obstacles.rects.push(rectAutour(c, 12, 14));
    });

    segments.forEach((sg) => {
        const a = pix(sg.a); const b = pix(sg.b);
        out.push(`<line class="sg" x1="${a[0]}" y1="${a[1]}" x2="${b[0]}" y2="${b[1]}" stroke="${sg.couleur}" stroke-width="2"${sg.dash ? ' stroke-dasharray="6 4"' : ''}/>`);
        obstacles.segs.push([a, b]);
    });

    const pointEn = (q) => points.some((p) => p.x === q[0] && p.y === q[1]);
    const geomFleches = fleches.map((f) => {
        const a = pix(f.a); const b = pix(f.b);
        const L = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
        const u = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
        const n = [-u[1], u[0]];
        const pointe = pointEn(f.b) ? [b[0] - 6 * u[0], b[1] - 6 * u[1]] : b;
        const base = [pointe[0] - 10 * u[0], pointe[1] - 10 * u[1]];
        const tri = [pointe, [base[0] + 5 * n[0], base[1] + 5 * n[1]], [base[0] - 5 * n[0], base[1] - 5 * n[1]]].map((c) => c.map(r1));
        const fin = [r1(base[0] + u[0]), r1(base[1] + u[1])];
        const col = f.couleur || VERT;
        out.push(`<g class="fl" data-de="${f.a[0]},${f.a[1]}" data-a="${f.b[0]},${f.b[1]}"${f.numero != null ? ` data-numero="${f.numero}"` : ''}><line x1="${a[0]}" y1="${a[1]}" x2="${fin[0]}" y2="${fin[1]}" stroke="${col}" stroke-width="2.4" stroke-linecap="round"/><polygon class="fl-pointe" points="${tri.map((c) => c.join(',')).join(' ')}" fill="${col}"/></g>`);
        obstacles.segs.push([a, pointe], [tri[1], tri[2]]);
        return { f, a, pointe, u, n, col };
    });

    points.forEach((p, i) => obstacles.rects.push({ ...rectAutour(pix([p.x, p.y]), 10, 10), point: i }));
    points.forEach((p, i) => {
        const c = pix([p.x, p.y]);
        const w = largeurNom(p.nom);
        const h = 14;
        const ordre = p.pref
            ? [...DIRECTIONS].sort((d1, d2) => {
                const ang = (d) => Math.acos(Math.max(-1, Math.min(1, (d[0] * p.pref[0] + d[1] * p.pref[1]) / (Math.hypot(...d) * (Math.hypot(...p.pref) || 1)))));
                return ang(d1) - ang(d2);
            })
            : DIRECTIONS;
        const candidats = ordre.map(([dx, dy]) => {
            // dy en coordonnées de grille (vers le haut) : l'écran l'inverse
            const ox = dx === 0 ? 0 : dx * (dy === 0 ? 7 + w / 2 : 5 + w / 2);
            const oy = dy === 0 ? 0 : -dy * (dx === 0 ? 7 + h / 2 : 5 + h / 2);
            return [c[0] + ox, c[1] + oy];
        });
        // Le disque du point lui-même n'est pas un obstacle pour son propre nom.
        const autour = { segs: obstacles.segs, rects: obstacles.rects.filter((r) => r.point !== i) };
        const pos = placerEtiquette(candidats, w, h, autour, cadre);
        obstacles.rects.push(pos.rect);
        const col = p.couleur || BLEU;
        out.push(`<g class="pt" data-nom="${p.nom}" data-xy="${p.x},${p.y}"><circle cx="${c[0]}" cy="${c[1]}" r="4" fill="${col}"/><text class="pl" x="${r1(pos.centre[0])}" y="${r1(pos.centre[1] + 5)}" text-anchor="middle" font-size="14" font-weight="700" fill="${col}">${p.nom}</text></g>`);
    });

    geomFleches.forEach(({ f, a, pointe, u, n, col }) => {
        if (f.numero == null) return;
        const m = [(a[0] + pointe[0]) / 2, (a[1] + pointe[1]) / 2];
        const t1 = [a[0] + (pointe[0] - a[0]) / 3, a[1] + (pointe[1] - a[1]) / 3];
        const candidats = [
            [m[0] + 16 * n[0], m[1] + 16 * n[1]], [m[0] - 16 * n[0], m[1] - 16 * n[1]],
            [a[0] - 16 * u[0], a[1] - 16 * u[1]],
            [t1[0] + 16 * n[0], t1[1] + 16 * n[1]], [t1[0] - 16 * n[0], t1[1] - 16 * n[1]],
        ];
        const pos = placerEtiquette(candidats, 20, 20, obstacles, cadre, 4);
        obstacles.rects.push(pos.rect);
        const [cx, cy] = pos.centre.map(r1);
        out.push(`<g class="nf" data-numero="${f.numero}"><circle cx="${cx}" cy="${cy}" r="9.5" fill="white" stroke="${col}" stroke-width="1.6"/><text x="${cx}" y="${r1(cy + 4.5)}" text-anchor="middle" font-size="13" font-weight="700" fill="${col}">${f.numero}</text></g>`);
    });

    out.push('</svg>');
    return out.join('');
};
